import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import { Loader } from "semantic-ui-react";
import { getWhatsAppConversations, getWhatsAppMessages, getWhatsAppApplicant } from "../../../../../apis/whatsapp";
import ConversationList from "../../../../../components/WhatsAppChat/ConversationList";
import MessageThread from "../../../../../components/WhatsAppChat/MessageThread";
import CustomerInfoPanel from "../../../../../components/WhatsAppChat/CustomerInfoPanel";
import socketService from "../../../../../services/socketService";
import { SOCKET_EVENTS } from "../../../../../constants/socketEvents";
import { useSubscription } from "../../../../../hooks";
import { filter } from "rxjs/operators";
import toastr from "toastr";
import "../../../../../components/WhatsAppChat/WhatsAppChat.scss";

const CONVERSATIONS_LIMIT = 30;

const WHATSAPP_SOCKET_EVENTS = [
  SOCKET_EVENTS.WHATSAPP.MESSAGE_INBOUND,
  SOCKET_EVENTS.WHATSAPP.MESSAGE_OUTBOUND,
];

function getCustomerPhone(msg) {
  return msg.direction === "inbound" ? msg.from : msg.to;
}

const WhatsAppPage = () => {
  const { phone: urlPhone } = useParams();
  const history = useHistory();
  const [conversations, setConversations] = useState([]);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [applicantDetails, setApplicantDetails] = useState(null);
  const [otherApplications, setOtherApplications] = useState([]);
  const [loadingApplicant, setLoadingApplicant] = useState(false);
  const selectedPhoneRef = useRef(null);

  const loadConversations = useCallback(async () => {
    setLoadingConversations(true);
    try {
      const response = await getWhatsAppConversations(0, CONVERSATIONS_LIMIT);
      const loaded = (response.data.conversations || []).map((c) => ({
        ...c,
        unreadCount: 0,
      }));
      setConversations(loaded);
    } catch (err) {
      toastr.error("Failed to load conversations");
    } finally {
      setLoadingConversations(false);
    }
  }, []);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  const handleSelectConversation = useCallback(async (conv) => {
    setSelectedConversation(conv);
    history.replace(`/whatsapp/${encodeURIComponent(conv.phoneNumber)}`);
    selectedPhoneRef.current = conv.phoneNumber;
    setMessages([]);
    setApplicantDetails(null);
    setOtherApplications([]);

    setConversations((prev) =>
      prev.map((c) =>
        c.phoneNumber === conv.phoneNumber ? { ...c, unreadCount: 0 } : c,
      ),
    );

    setLoadingMessages(true);
    try {
      const response = await getWhatsAppMessages(conv.phoneNumber);
      if (selectedPhoneRef.current !== conv.phoneNumber) return;
      const fetched = response.data.messages || [];
      setMessages((current) => {
        // Preserve socket messages that arrived while the HTTP fetch was in-flight
        const fetchedIds = new Set(fetched.map((m) => m._id).filter(Boolean));
        const socketOnly = current.filter((m) => m._id && !fetchedIds.has(m._id));
        return [...fetched, ...socketOnly];
      });
    } catch (err) {
      toastr.error("Failed to load messages");
    } finally {
      setLoadingMessages(false);
    }

    setLoadingApplicant(true);
    getWhatsAppApplicant(conv.phoneNumber)
      .then((res) => {
        if (selectedPhoneRef.current === conv.phoneNumber) {
          setApplicantDetails(res.data.applicant);
          setOtherApplications(res.data.otherApplications || []);
        }
      })
      .catch(() => {})
      .finally(() => setLoadingApplicant(false));
  }, [history]);

  const autoSelectedRef = useRef(false);
  useEffect(() => {
    if (!urlPhone || autoSelectedRef.current || loadingConversations) return;
    autoSelectedRef.current = true;
    const decoded = decodeURIComponent(urlPhone);
    const match = conversations.find((c) => c.phoneNumber === decoded);
    handleSelectConversation(match || { phoneNumber: decoded, unreadCount: 0 });
  }, [urlPhone, conversations, loadingConversations, handleSelectConversation]);

  const upsertConversation = useCallback((msg, applicant) => {
    const phone = getCustomerPhone(msg);
    if (!phone) return;

    setConversations((prev) => {
      const existingIndex = prev.findIndex((c) => c.phoneNumber === phone);
      const isActiveConversation = selectedPhoneRef.current === phone;
      const isUnreadInbound = msg.direction === "inbound" && !isActiveConversation;

      const applicantName = applicant
        ? `${applicant.firstName || ""} ${applicant.lastName || ""}`.trim() || null
        : null;

      const baseConv = {
        phoneNumber: phone,
        lastMessage: msg.body,
        lastMessageDirection: msg.direction,
        lastMessageAt: msg.createdAt,
        applicantName,
        applicantId: applicant?._id,
        zendeskId: applicant?.zendeskId,
        product: applicant?.api,
      };

      if (existingIndex >= 0) {
        const existing = prev[existingIndex];
        const unreadCount = isActiveConversation
          ? 0
          : (existing.unreadCount || 0) + (isUnreadInbound ? 1 : 0);
        const merged = { ...existing, ...baseConv, unreadCount };
        const remaining = [...prev];
        remaining.splice(existingIndex, 1);
        return [merged, ...remaining];
      }

      return [{ ...baseConv, unreadCount: isUnreadInbound ? 1 : 0 }, ...prev];
    });
  }, []);

  const appendMessageToThread = useCallback((msg) => {
    setMessages((prev) => {
      const isDuplicate = msg._id && prev.some((m) => m._id === msg._id);
      if (isDuplicate) return prev;
      return [...prev, msg];
    });
  }, []);

  const handleSocketMessage = useCallback(
    (event) => {
      const { message, applicant } = event.data || {};
      if (!message) return;

      const msgPhone = getCustomerPhone(message);
      if (selectedPhoneRef.current === msgPhone) {
        appendMessageToThread(message);
      }
      upsertConversation(message, applicant);
    },
    [appendMessageToThread, upsertConversation],
  );

  useSubscription(
    socketService.socketSubject,
    handleSocketMessage,
    [filter((event) => WHATSAPP_SOCKET_EVENTS.includes(event.type))],
  );

  const totalUnread = conversations.reduce((sum, c) => sum + (c.unreadCount || 0), 0);

  return (
    <div className="wa-chat-page">
      <div className="wa-chat-page__sidebar">
        <div className="wa-chat-page__sidebar-header">
          <h2>
            WhatsApp
            {totalUnread > 0 && (
              <span className="wa-unread-badge">{totalUnread}</span>
            )}
          </h2>
        </div>
        <ConversationList
          conversations={conversations}
          selectedPhone={selectedConversation?.phoneNumber}
          onSelect={handleSelectConversation}
          loading={loadingConversations}
        />
      </div>

      <div className="wa-chat-page__main">
        {loadingMessages ? (
          <div className="wa-chat-page__loader">
            <Loader active inline="centered" />
          </div>
        ) : (
          <MessageThread
            conversation={selectedConversation}
            messages={messages}
            onMessagesLoaded={setMessages}
          />
        )}
      </div>

      {selectedConversation && (
        <div className="wa-chat-page__info-panel">
          <CustomerInfoPanel
            applicant={applicantDetails}
            otherApplications={otherApplications}
            conversation={selectedConversation}
            loading={loadingApplicant}
          />
        </div>
      )}
    </div>
  );
};

export default WhatsAppPage;
